/*
 * Copyright (C) [2025] [Netzint GmbH]
 * All rights reserved.
 *
 * This software is dual-licensed under the terms of:
 *
 * 1. The GNU Affero General Public License (AGPL-3.0-or-later), as published by the Free Software Foundation.
 *    You may use, modify and distribute this software under the terms of the AGPL, provided that you comply with its conditions.
 *
 *    A copy of the license can be found at: https://www.gnu.org/licenses/agpl-3.0.html
 *
 * OR
 *
 * 2. A commercial license agreement with Netzint GmbH. Licensees holding a valid commercial license from Netzint GmbH
 *    may use this software in accordance with the terms contained in such written agreement, without the obligations imposed by the AGPL.
 *
 * If you are uncertain which license applies to your use case, please contact us at info@netzint.de for clarification.
 */

import React from 'react';
import { useDropzone, type DropzoneOptions } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';
import cn from '../utils/cn';

export interface DropZoneProps {
  onDrop: DropzoneOptions['onDrop'];
  accept?: Record<string, string[]>;
  dragActiveText: string;
  inactiveText: string;
  className?: string;
  minHeight?: string;
  getFilesFromEvent?: DropzoneOptions['getFilesFromEvent'];
  activeClassName?: string;
  inactiveClassName?: string;
}

const DropZone: React.FC<DropZoneProps> = ({
  onDrop,
  accept,
  dragActiveText,
  inactiveText,
  className,
  minHeight = 'min-h-32',
  getFilesFromEvent,
  activeClassName = 'bg-accent-light',
  inactiveClassName = 'bg-background dark:bg-accent',
}) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    ...(getFilesFromEvent ? { getFilesFromEvent } : {}),
  });

  const dropzoneStyle = cn(
    'border-2 border-dashed border-muted dark:border-muted-foreground rounded-lg cursor-pointer',
    isDragActive ? activeClassName : inactiveClassName,
    className,
  );

  return (
    <div {...getRootProps({ className: dropzoneStyle })}>
      <input {...getInputProps()} />
      <div className={cn('flex flex-col items-center justify-center space-y-2 p-4', minHeight)}>
        <p className="text-wrap text-center text-sm text-muted-foreground">
          {isDragActive ? dragActiveText : inactiveText}
        </p>
        <FontAwesomeIcon
          icon={faCloudArrowUp}
          className="h-10 w-10 text-muted-foreground"
        />
      </div>
    </div>
  );
};

export default DropZone;
